'use client';

import * as React from 'react';
import { Box, Typography, Divider, Chip } from '@unity/cloud-ui';
import { useTranslations } from 'next-intl';
import themeConstants from '@/components/theme/theme-constants';
import { ThemeContext } from '@/components/theme';
import { scrollToAnchor } from '@/utils/scroll-to-anchor';
import { Field } from '../field';
import { Code, CodeGroup } from '../code';
import { Header } from '../header';
import { Accordion } from '../accordion';
import { ResponseSchemaAccordion } from './components/response-schema-accordion';
import { generateCurlCommand } from './utils';
import { generateExampleFromSchema } from './utils/generate-example-from-schema';
import type {
  OpenAPISpec,
  OpenAPIServer,
  OpenAPIParameter,
  OpenAPIOperation,
  OpenAPIPathItem,
  OpenAPIResponse,
  OpenAPIMediaType,
} from './types/types';

interface OpenAPIPublicProps {
  spec: OpenAPISpec;
}

/**
 * Get the media type from a response, checking common content types
 * Returns both the media type object and the content type string
 * Falls back to the first available content type if no known types match
 */
const getMediaType = (
  response: OpenAPIResponse,
): { mediaType: OpenAPIMediaType | undefined; contentType: string | undefined } => {
  const commonContentTypes = [
    'application/json',
    'application/problem+json',
    'application/scim+json',
    'application/vnd.api+json',
    'application/hal+json',
    'application/ld+json',
    'application/xml',
    'text/plain',
    'text/html',
  ];

  // Check common content types first
  for (const contentType of commonContentTypes) {
    if (response.content?.[contentType]) {
      return { mediaType: response.content[contentType], contentType };
    }
  }

  // Fall back to the first available content type (handles custom/vendor types)
  if (response.content) {
    const availableContentTypes = Object.keys(response.content);
    if (availableContentTypes.length > 0) {
      const firstContentType = availableContentTypes[0];
      return { mediaType: response.content[firstContentType], contentType: firstContentType };
    }
  }

  return { mediaType: undefined, contentType: undefined };
};

/**
 * Get example data from a media type, either explicit or generated from schema
 */
const getExampleData = (mediaType: OpenAPIMediaType | undefined): unknown => {
  if (!mediaType) {
    return null;
  }

  // Check for explicit example in mediaType
  if (mediaType.example !== undefined) {
    return mediaType.example;
  }

  // Check for examples object
  if (mediaType.examples && Object.keys(mediaType.examples).length > 0) {
    const firstExample = Object.values(mediaType.examples)[0];
    return typeof firstExample === 'object' && firstExample !== null && 'value' in firstExample
      ? (firstExample as { value: unknown }).value
      : firstExample;
  }

  // Check for example in schema
  if (mediaType.schema?.example !== undefined) {
    return mediaType.schema.example;
  }

  // Generate example from schema
  if (mediaType.schema) {
    return generateExampleFromSchema(mediaType.schema);
  }

  return null;
};

export const OpenAPIPublic = ({ spec }: OpenAPIPublicProps) => {
  const { info, servers, paths } = spec;
  const { zoom } = React.useContext(ThemeContext);
  const t = useTranslations();

  /**
   * Render a single parameter field
   */
  const renderParameterField = (param: OpenAPIParameter, idx: number) => (
    <Box key={`${param.name}-${idx}`} sx={{ ml: 2, mb: 2 }}>
      <Field
        title={param.name}
        type={param.schema?.type}
        required={param.required}
        default={param.schema?.default !== undefined ? `${param.schema?.default}` : undefined}
        example={param.example !== undefined ? `${param.example}` : undefined}
      >
        {param.description || t('mdx:openapi:no-description')}
      </Field>
    </Box>
  );

  /**
   * Render a section of parameters with a header
   */
  const renderParameterSection = (params: OpenAPIParameter[], titleKey: string, title: string) => {
    if (params.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 1 }}>
        <Header variant="h3" sx={{ mb: 3 }}>
          {t(titleKey, { title })}
        </Header>
        {params.map((param, idx) => renderParameterField(param, idx))}
      </Box>
    );
  };

  const {
    layout: { headerHeight, productHeaderHeight },
  } = themeConstants;

  // Scroll to anchor after content has loaded
  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash.slice(1);

      const timeoutId = setTimeout(() => {
        scrollToAnchor(hash, { behavior: 'smooth' });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [paths]);

  return (
    <Box
      sx={(theme) => ({
        py: theme.spacing(4),
        zoom: themeConstants.zoom[zoom],
        [theme.breakpoints.up('lg')]: {
          px: theme.spacing(12),
        },
        [theme.breakpoints.down('lg')]: {
          px: theme.spacing(3),
        },
        '.MuiTypography-body1': {
          lineHeight: '1.5rem',
        },
        h1: {
          fontSize: '32px',
          lineHeight: '40px',
          letterSpacing: '-0.01rem',
        },
        h2: {
          fontSize: '19px',
          lineHeight: '23px',
          letterSpacing: '-0.019rem',
        },
      })}
    >
      {/* Easter egg comment */}
      <div dangerouslySetInnerHTML={{ __html: '<!-- Imitation is the greatest form of flattery -->' }} />
      {/* API Info */}
      {info && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" gutterBottom>
            {info.title}
          </Typography>
          {info.description && (
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: 400, fontSize: '1rem', lineHeight: '1.5rem', color: 'text.secondary' }}
            >
              {info.description}
            </Typography>
          )}
        </Box>
      )}

      {/* Paths/Endpoints */}
      {paths && (
        <Box sx={{ mb: 4 }}>
          {Object.entries(paths).map(([path, pathItem]: [string, OpenAPIPathItem]) => (
            <Box key={path}>
              {Object.entries(pathItem).map(([method, operation]) => {
                // Only process HTTP methods (objects), skip path-level properties (strings/arrays)
                if (typeof operation !== 'object' || operation === null || Array.isArray(operation)) {
                  return null;
                }
                const op = operation as OpenAPIOperation;
                const title = op.summary || `${method.toUpperCase()} ${path}`;

                // Merge path-level and operation-level parameters
                const pathLevelParams = (pathItem.parameters as OpenAPIParameter[]) || [];
                const operationLevelParams = (op.parameters as OpenAPIParameter[]) || [];
                const allParameters = [...pathLevelParams, ...operationLevelParams];

                // Group parameters by location (in)
                const pathParams = allParameters.filter((p) => p.in === 'path');
                const headerParams = allParameters.filter((p) => p.in === 'header');
                const queryParams = allParameters.filter((p) => p.in === 'query');
                const cookieParams = allParameters.filter((p) => p.in === 'cookie');

                return (
                  <Box key={`${path}-${method}`}>
                    <Header variant="h2">{title}</Header>
                    <Divider sx={{ mt: 2, mb: 4 }} />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                        gap: 4,
                        mb: 4,
                        pb: 4,
                      }}
                    >
                      {/* Left column */}
                      <Box sx={{ minWidth: 0 }} display="flex" flexDirection="column" gap={6}>
                        {op.description && <Typography>{op.description}</Typography>}

                        {renderParameterSection(pathParams, 'mdx:openapi:path-parameters-for', title)}
                        {renderParameterSection(headerParams, 'mdx:openapi:header-parameters-for', title)}
                        {renderParameterSection(queryParams, 'mdx:openapi:query-parameters-for', title)}
                        {renderParameterSection(cookieParams, 'mdx:openapi:cookie-parameters-for', title)}

                        {op.responses && Object.keys(op.responses).length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Header variant="h3" sx={{ mb: 3 }}>
                              {t('mdx:openapi:http-response-status-codes-for', { title })}
                            </Header>
                            {Object.entries(op.responses).map(([statusCode, response]: [string, OpenAPIResponse]) => {
                              const { mediaType, contentType } = getMediaType(response);
                              const schema = mediaType?.schema;

                              return (
                                <ResponseSchemaAccordion
                                  key={statusCode}
                                  statusCode={statusCode}
                                  response={response}
                                  schema={schema}
                                  contentType={contentType}
                                  t={t}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>

                      {/* Right column */}
                      <Box
                        sx={{
                          position: { xs: 'relative', lg: 'sticky' },
                          top: { xs: 0, lg: `calc(${headerHeight} + ${productHeaderHeight} + 1rem)` },
                          alignSelf: 'start',
                          minWidth: 0,
                        }}
                      >
                        <Header variant="h3" gutterBottom sx={{ mb: 2 }}>
                          {t('mdx:openapi:code-samples-for', { title })}
                        </Header>
                        <Header variant="h4" gutterBottom sx={{ mb: 2 }}>
                          {t('mdx:openapi:request-example')}
                        </Header>
                        <CodeGroup>
                          {servers && servers.length > 0 ? (
                            servers.map((server: OpenAPIServer, index: number) => {
                              const fullUrl = `${server.url}${path}`;
                              const curlCommand = generateCurlCommand(method, fullUrl, op, spec);
                              return (
                                <pre
                                  key={`${server.url}-${index}`}
                                  title={t('mdx:openapi:server', { number: index + 1 })}
                                >
                                  <Code className="language-bash">{curlCommand}</Code>
                                </pre>
                              );
                            })
                          ) : (
                            <pre title="cURL">
                              <Code className="language-bash">{generateCurlCommand(method, path, op, spec)}</Code>
                            </pre>
                          )}
                        </CodeGroup>
                        <Header variant="h4" sx={{ my: 2 }}>
                          {t('mdx:openapi:response-example')}
                        </Header>
                        <Accordion
                          title={
                            <Box display="flex" alignItems="center">
                              <Chip label={method.toUpperCase()} color={'info'} sx={{ mr: 1, py: 0.5 }} />
                              <Typography variant="body2" color="text.secondary">
                                {path}
                              </Typography>
                            </Box>
                          }
                          sxOverride={{ padding: 0, borderTop: 'none' }}
                        >
                          <CodeGroup sxOverride={{ marginY: 0, borderRadius: 0 }}>
                            {op.responses &&
                              Object.keys(op.responses).length > 0 &&
                              Object.entries(op.responses).flatMap(
                                ([statusCode, response]: [string, OpenAPIResponse]) => {
                                  const { mediaType } = getMediaType(response);
                                  const exampleData = getExampleData(mediaType);

                                  if (exampleData === null) {
                                    return [];
                                  }

                                  const exampleJson = JSON.stringify(exampleData, null, 2);

                                  return [
                                    <pre key={`example-${statusCode}`} title={statusCode}>
                                      <Code className="language-json">{exampleJson}</Code>
                                    </pre>,
                                  ];
                                },
                              )}
                          </CodeGroup>
                        </Accordion>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
